import { Controller } from "@hotwired/stimulus"

// Offers the auction prices and marketing packages of the auction's year and the agency's country, and the offer types
// those auction prices are offered for, and asks for what the auction type, the agency's country and the chosen auction
// price need: the offer type, the venue or the additional services.
export default class extends Controller {
  static values = {
    auctionPrices: Array,
    marketingPackages: Array,
    servicesUnrequestedCountries: Array
  }

  static targets = [
    "agency",
    "auctionPrice",
    "auctionType",
    "includedItems",
    "includedItemsLead",
    "logisticsPricing",
    "marketingPackage",
    "offerType",
    "offerTypeFields",
    "service",
    "services",
    "startTime"
  ]

  static classes = ["hidden"]

  #offerTypes = []

  connect() {
    this.#offerTypes = [...this.offerTypeTarget.options].map(({ value, text }) => ({ id: value, label: text }))
    this.refresh()
  }

  // An event is priced whatever it offers.
  refresh() {
    this.#toggle(this.offerTypeFieldsTarget, this.auctionTypeTarget.value !== "event")
    this.#updateOfferTypes()
    this.#updateAuctionPrices()
    this.#updateMarketingPackages()
  }

  toggleLogisticsPricing() {
    this.#toggle(this.logisticsPricingTarget, this.#selectedAuctionPrice?.logisticsPricing ?? false)
  }

  // The agency picks additional marketing services unless its country leaves them to the admin.
  updateForPackage() {
    this.#listIncludedItems(this.#selectedPackage)
    this.#toggle(this.servicesTarget, !this.#servicesUnrequested)

    this.serviceTargets.forEach(service => {
      this.#toggle(service, service.dataset.year === this.#year && service.dataset.country === this.#country)
    })
  }

  get #year() {
    return this.startTimeTarget.value.slice(0, 4)
  }

  get #country() {
    return this.agencyTarget.selectedOptions[0]?.dataset.country ?? ""
  }

  get #servicesUnrequested() {
    return this.servicesUnrequestedCountriesValue.includes(this.#country)
  }

  get #offerType() {
    return this.offerTypeFieldsTarget.disabled ? "" : this.offerTypeTarget.value
  }

  // The auction prices of the auction type in the auction's year and the agency's country.
  get #auctionPricesOfAuction() {
    return this.auctionPricesValue.filter(
      ({ year, country, auctionType }) => auctionType === this.auctionTypeTarget.value && year === this.#year && country === this.#country
    )
  }

  get #selectedAuctionPrice() {
    return this.auctionPricesValue.find(({ id }) => id === this.auctionPriceTarget.value)
  }

  get #selectedPackage() {
    return this.marketingPackagesValue.find(({ id }) => id === this.marketingPackageTarget.value)
  }

  // The offer types that one of the auction's auction prices is offered for, so each one picked has a price.
  #updateOfferTypes() {
    const offered = this.#auctionPricesOfAuction.flatMap(({ offerTypes }) => offerTypes)

    this.#replaceOptions(
      this.offerTypeTarget,
      this.#offerTypes.filter(({ id }) => id === "" || offered.includes(id))
    )
  }

  // The auction's auction prices that suit the offer type.
  #updateAuctionPrices() {
    const offerType = this.#offerType
    const auctionPrices = this.#auctionPricesOfAuction.filter(({ offerTypes }) => offerType === "" || offerTypes.includes(offerType))

    this.#replaceOptions(this.auctionPriceTarget, auctionPrices)
    this.toggleLogisticsPricing()
  }

  // The packages of the auction's year and the agency's country that suit the offer type, starting at the standard one.
  #updateMarketingPackages() {
    const offerType = this.#offerType
    const packages = this.marketingPackagesValue.filter(
      ({ year, country, offerTypes }) =>
        year === this.#year && country === this.#country && (offerType === "" || offerTypes.includes(offerType))
    )

    this.#replaceOptions(this.marketingPackageTarget, packages, packages.find(({ standard }) => standard)?.id)
    this.updateForPackage()
  }

  // Keeps the selection where it is still offered.
  #replaceOptions(select, records, fallbackId) {
    const selected = select.value
    select.replaceChildren(...records.map(({ id, label }) => new Option(label, id)))
    select.value = selected
    if (select.selectedIndex === -1 && fallbackId) select.value = fallbackId
    if (select.selectedIndex === -1) select.selectedIndex = 0
  }

  // An upgrade package comes on top of the standard marketing, as the quote words it.
  #listIncludedItems(selectedPackage) {
    const upgrade = selectedPackage && !selectedPackage.standard && !selectedPackage.full
    this.includedItemsLeadTarget.textContent = upgrade ? "Standard marketing, plus:" : "Includes:"

    const items = selectedPackage?.includedItems ?? []
    this.includedItemsTarget.replaceChildren(
      ...items.map(text => {
        const item = document.createElement("li")
        item.textContent = text
        return item
      })
    )
  }

  #toggle(element, shown) {
    element.classList.toggle(this.hiddenClass, !shown)
    element.disabled = !shown
  }
}
